import { Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useAtom } from 'jotai'
import { orderHistoryAtom, orderHistoryErrorAtom, orderHistoryIsLoadingAtom, TOrderHistoryRow } from '@/core/store/orderHistory.store'
import { getOrderHistory } from '@/core/service/service'
import { base64Encode, formattedDate } from '@/core/utils/utils'
import ModalOrderDetails from './modalOrderDetails.layout'
import { CUSTOMER_INFORMATION_KEY, DATA_DISTRIBUTOR_KEY } from '@/core/constants/auth'

const OrderHistoryLayout = () => {
    const [orderHistory, setOrderHistory] = useAtom(orderHistoryAtom)
    const [isLoading, setIsLoading] = useAtom(orderHistoryIsLoadingAtom)
    const [errorMessage, setErrorMessage] = useAtom(orderHistoryErrorAtom)

    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [openModal, setOpenModal] = useState(false)
    const [selectedOrder, setSelectedOrder] = useState<TOrderHistoryRow | null>(null)

    const currentBaid = JSON.parse(sessionStorage.getItem(DATA_DISTRIBUTOR_KEY) ?? '{}').baid ?? ''
    const currentName = JSON.parse(sessionStorage.getItem(DATA_DISTRIBUTOR_KEY) ?? '{}').name ?? ''
    const cstoken = JSON.parse(sessionStorage.getItem(CUSTOMER_INFORMATION_KEY) ?? '{}').token ?? ''
    const customerHref = JSON.parse(sessionStorage.getItem(CUSTOMER_INFORMATION_KEY) ?? '{}').href ?? ''

    const encodedHref = useMemo(() => (customerHref ? base64Encode(customerHref) : ''), [customerHref])

    const fetchOrders = async (dateStart?: string, isLoadMore = false) => {
        if (!cstoken || !encodedHref || !customerHref) return

        const toggleLoading = isLoadMore ? setIsLoadingMore : setIsLoading
        toggleLoading(true)

        try {
            setErrorMessage('')
            const response = await getOrderHistory({
                cstoken,
                baHrefBase64: encodedHref,
                dateStart
            })

            if (response?.data?.success && Array.isArray(response.data.data)) {
                const incoming = response.data.data as TOrderHistoryRow[]

                setOrderHistory(prev => {
                    if (!isLoadMore) {
                        return incoming
                    }

                    const existingIds = new Set(prev.map(item => item.order_id))
                    const merged = [...prev]

                    incoming.forEach(item => {
                        if (item && !existingIds.has(item.order_id)) {
                            merged.push(item)
                        }
                    })

                    return merged
                })
            } else if (!isLoadMore) {
                setOrderHistory([])
                setErrorMessage('Không có dữ liệu lịch sử đơn hàng')
            }
        } catch (error) {
            if (!isLoadMore) {
                setOrderHistory([])
            }
            setErrorMessage('Không thể lấy lịch sử đơn hàng')
        } finally {
            toggleLoading(false)
        }
    }

    useEffect(() => {
        if (!currentBaid || !cstoken || !encodedHref) {
            return
        }

        fetchOrders()
    }, [])

    const handleOpenModal = (order: TOrderHistoryRow) => {
        setSelectedOrder(order)
        setOpenModal(true)
    }

    const handleLoadMore = async () => {
        if (isLoadingMore || orderHistory.length === 0) return

        const lastOrder = orderHistory[orderHistory.length - 1]
        const dateStart = lastOrder?.order_detail?.terms?.period

        if (!dateStart) return

        await fetchOrders(dateStart, true)
    }

    return (
        <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, boxShadow: '0px 8px 24px rgba(15,15,15,0.06)' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="flex-start" spacing={2} mb={3}>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                    Lịch sử giao dịch ({currentBaid || '-'}, {currentName || '-'})
                </Typography>
            </Stack>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <Table
                    sx={{
                        '& .MuiTableCell-root': {
                            borderBottom: 'none',
                            textAlign: 'center'
                        }
                    }}
                    size="medium">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <TableCell sx={{ fontWeight: 600, borderRight: '1px solid #dee2e6' }}>Số đơn hàng</TableCell>
                            <TableCell sx={{ fontWeight: 600, borderRight: '1px solid #dee2e6' }}>Ngày đơn hàng</TableCell>
                            <TableCell sx={{ fontWeight: 600, borderRight: '1px solid #dee2e6' }}>Tháng hoa hồng</TableCell>
                            <TableCell sx={{ fontWeight: 600, borderRight: '1px solid #dee2e6' }}>Tổng PV</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Hoạt động</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={5} sx={{ py: 6, textAlign: 'center' }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : errorMessage ? (
                            <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                                <TableCell colSpan={5} sx={{ py: 4, color: 'error.main', textAlign: 'center', fontWeight: 600 }}>
                                    {errorMessage}
                                </TableCell>
                            </TableRow>
                        ) : orderHistory.length === 0 ? (
                            <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                                <TableCell colSpan={5} sx={{ py: 4, color: 'text.secondary', textAlign: 'center' }}>
                                    Không có dữ liệu
                                </TableCell>
                            </TableRow>
                        ) : (
                            orderHistory.map((row, index) => {
                                const detail = row.order_detail ?? {}
                                const displayDate = detail.dateCreated ? formattedDate(detail.dateCreated) : '-'
                                const displayPeriod = detail.terms?.period ?? '-'
                                const displayPv = detail.terms?.pv ?? '-'

                                return (
                                    <TableRow key={`${row.order_id}-${index}`} sx={{ backgroundColor: index % 2 === 0 ? 'rgba(0,0,0,0.04)' : '#FFF' }}>
                                        <TableCell sx={{ borderRight: '1px solid #dee2e6' }}>{row.order_id}</TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #dee2e6' }}>{displayDate}</TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #dee2e6' }}>{displayPeriod}</TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #dee2e6' }}>{displayPv}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                sx={{ textTransform: 'capitalize', background: '#007bff' }}
                                                onClick={() => handleOpenModal(row)}>
                                                Xem đơn hàng
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
            </Table>
        </TableContainer>

        {orderHistory.length > 0 && (
            <Stack direction="row" justifyContent="center" mt={3}>
                <Button variant="outlined" onClick={handleLoadMore} disabled={isLoadingMore} sx={{ minWidth: 160, height: 40 }}>
                    {isLoadingMore ? <CircularProgress size={20} /> : 'Xem thêm'}
                </Button>
            </Stack>
        )}

        <ModalOrderDetails
            open={openModal}
            onClose={() => setOpenModal(false)}
            order={selectedOrder}
            customerName={currentName}
            customerId={currentBaid}
        />
    </Paper>
  )
}

export default OrderHistoryLayout